import { Controller } from '@hotwired/stimulus';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;
const TICK_INTERVAL_MS = 30 * 1000;

const BASE = 'votes--dashboard--next-event-component';
const DANGER_CLASS = `${BASE}--danger`;

export default class extends Controller {
  static targets = ['countdown'];
  static values = {
    name: String,
    kind: String,
    at: String,
  };

  declare readonly countdownTarget: HTMLElement;
  declare readonly nameValue: string;
  declare readonly kindValue: string;
  declare readonly atValue: string;

  private intervalId: number | null = null;

  connect(): void {
    this.tick();
    this.intervalId = window.setInterval(() => this.tick(), TICK_INTERVAL_MS);
  }

  disconnect(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  dismiss(): void {
    this.element.setAttribute('hidden', 'hidden');
    this.disconnect();
  }

  private tick(): void {
    const target = Date.parse(this.atValue);
    if (Number.isNaN(target)) {
      return;
    }

    const remainingMs = target - Date.now();

    if (remainingMs <= 0) {
      this.dismiss();
      return;
    }

    if (remainingMs > ONE_WEEK_MS) {
      this.element.setAttribute('hidden', 'hidden');
      return;
    }
    this.element.removeAttribute('hidden');

    this.countdownTarget.textContent = `${this.verb()} in ${this.formatDuration(remainingMs)}`;
    this.element.classList.toggle(DANGER_CLASS, remainingMs < ONE_HOUR_MS);
  }

  private verb(): string {
    return this.kindValue === 'ends' ? 'ends' : 'starts';
  }

  private formatDuration(ms: number): string {
    if (ms >= ONE_DAY_MS) {
      const days = Math.floor(ms / ONE_DAY_MS);
      const hours = Math.floor((ms % ONE_DAY_MS) / ONE_HOUR_MS);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }

    const totalMinutes = Math.max(1, Math.floor(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours <= 0) {
      return `${minutes}m`;
    }

    return `${hours}h ${minutes}m`;
  }
}
